import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import { Button, Menu, MenuButton, MenuContent, MenuList } from '@apollo/orbit';
import React from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';

import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { UserPermission } from 'src/lib/graphqlTypes/types';

import { GraphCreationOptions } from './GraphCreationOptions';

/**
 * GraphCreationButton
 *
 * Renders a button for creating new graphs, with behavior and appearance
 * that varies based on user permissions, plan type, feature flags, and available capabilities.
 *
 * Logic:
 * 1. Permission-Based Rendering:
 *    - If user lacks 'canCreateService' permission, the button is disabled with a tooltip
 *      explaining why.
 * 2. Button Type Selection:
 *    - When menu items are available: Uses the Menu component with MenuButton
 *    - When no menu items are available: Uses a regular Button component that links directly
 *      to creating a graph with a GraphQL API
 * 3. Button Destination Logic:
 *    - For Cloud-Only Plans (Dedicated or Serverless): Links to the flow for cloud graph creation
 */
export const GraphCreationButton = ({
  accountRole,
  accountId,
  isCloudOnlyPlan,
}: {
  accountRole: UserPermission | undefined;
  accountId: string;
  isCloudOnlyPlan: boolean;
}) => {
  const { menuItems, defaultAction } = GraphCreationOptions({
    accountId,
    isCloudOnlyPlan,
  });

  return (
    <PermissionWarning
      placement="left"
      accountId={accountId}
      accountRole={accountRole}
      accountPermissions="canCreateService"
      childRenderer={(hasPermission) => {
        // When no extra menu items are available, render a direct action button
        return menuItems.length > 1 ? (
          <Menu placement="bottom-end">
            <MenuButton
              as={Button}
              rightIcon={<IconChevronDown />}
              variant="primary"
              size="md"
              data-testid="create-graph-button"
              isDisabled={!hasPermission}
            >
              Add graph
            </MenuButton>
            <MenuContent key="rover-init" className="max-w-72 shadow-md">
              <MenuList>{menuItems}</MenuList>
            </MenuContent>
          </Menu>
        ) : (
          <Button
            as={ReactRouterLink}
            to={defaultAction.to}
            onClick={defaultAction.onClick}
            variant="primary"
            size="md"
            data-testid="create-graph-button"
            isDisabled={!hasPermission}
          >
            Add graph
          </Button>
        );
      }}
    />
  );
};
